import { MailboxProvider } from '~/api/graphql';

/**
 * Everything the UI says about one mail provider. Sentences that differ only by
 * a noun interpolate a field from here rather than branching on the provider,
 * so a second provider is a second entry and nothing else.
 */
export type MailboxProviderProfile = {
  name: string;
  logo: string;
  /** What the provider does for Cara, as badges beside its name. */
  capabilities: string[];
  /** Whether settings may offer monitoring to this mailbox's owner. */
  supportsMonitoring: boolean;
  /**
   * Where an unsent draft waits, in the provider's own words. Outlook has a
   * Drafts folder; a provider that files mail differently needs its own noun
   * here rather than a sentence that lies about the destination.
   */
  draftsLocation: string;
};

/**
 * Keyed by the enum rather than by string, so a provider added to the schema
 * fails to compile until it says everything the UI needs about itself.
 */
export const mailboxProviders: Record<MailboxProvider, MailboxProviderProfile> = {
  [MailboxProvider.Outlook]: {
    name: 'Outlook',
    logo: '/images/outlook.png',
    capabilities: ['Email', 'Send'],
    supportsMonitoring: true,
    draftsLocation: 'Drafts folder'
  }
};

export const mailboxProviderList: MailboxProvider[] = Object.values(MailboxProvider);

/**
 * The provider slug pkg/service/api/http/mailbox_oauth.go matches on. It is the
 * enum member lowercased, so it stays correct for a provider nobody has written
 * a display name for yet.
 */
export const mailboxProviderSlug = (provider: MailboxProvider): string => provider.toLowerCase();

/**
 * What to call a mailbox whose provider is not known here. Reads in the same
 * sentences the named providers do.
 */
export const unknownMailClientName = 'your mailbox';

export const mailClientName = (provider?: MailboxProvider): string =>
  provider ? mailboxProviders[provider].name : unknownMailClientName;

export const draftsLocationName = (provider?: MailboxProvider): string =>
  provider ? mailboxProviders[provider].draftsLocation : 'Drafts folder';
